import axios from 'axios'
import moment from 'moment'

const state = {
  attendanceRecords: [],
  currentRecord: null,
  locations: [], // Thêm state lưu trữ danh sách địa điểm chấm công
  loading: false, // Thêm state loading
  filters: {
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
    locationId: null,
    employeeId: null,
    shiftId: null
  },
  statistics: {
    total: 0,
    onTime: 0,
    late: 0,
    early: 0
  }
}

const getters = {
  allRecords: state => state.attendanceRecords,
  currentRecord: state => state.currentRecord,
  currentFilters: state => state.filters,
  statistics: state => state.statistics,
  locations: state => state.locations,
  loading: state => state.loading,

  // Lọc dữ liệu theo nhân viên
  getRecordsByEmployee: state => employeeId => {
    return state.attendanceRecords.filter(record => record.employeeId === employeeId)
  },

  // Lọc dữ liệu theo địa điểm
  getRecordsByLocation: state => locationId => {
    return state.attendanceRecords.filter(record => record.locationId === locationId)
  },

  // Lọc dữ liệu theo ca
  getRecordsByShift: state => shiftId => {
    return state.attendanceRecords.filter(record => record.shiftId === shiftId)
  },

  // Thống kê theo ngày
  getDailyStatistics: state => date => {
    const dayRecords = state.attendanceRecords.filter(
      record => moment(record.checkInTime).format('YYYY-MM-DD') === date
    )
    return {
      total: dayRecords.length,
      onTime: dayRecords.filter(r => !r.isLate && !r.isEarly).length,
      late: dayRecords.filter(r => r.isLate).length,
      early: dayRecords.filter(r => r.isEarly).length
    }
  }
}

const actions = {
  // Lấy dữ liệu chấm công theo bộ lọc - Thêm alias fetchRecords cho tương thích
  async fetchRecords({ commit, state }, filters) {
    try {
      // Sử dụng filters được truyền vào hoặc dùng state.filters nếu không có
      const queryParams = filters || state.filters

      const response = await axios.get('/attendance', {
        params: queryParams
      })
      
      // Đảm bảo dữ liệu có cấu trúc đúng với component yêu cầu
      let records = []
      let stats = {
        total: 0,
        onTime: 0,
        late: 0,
        early: 0
      }
      
      // Kiểm tra và chuyển đổi cấu trúc dữ liệu từ API
      if (response.data) {
        if (response.data.records) {
          records = response.data.records
          stats = response.data.statistics || stats
        } else if (Array.isArray(response.data)) {
          records = response.data
          // Tính toán thống kê nếu API không trả về
          stats.total = records.length
          stats.onTime = records.filter(r => r.status === 'on_time').length
          stats.late = records.filter(r => r.status === 'late').length
          stats.early = records.filter(r => r.status === 'early').length
        }
      }
      
      commit('setAttendanceRecords', records)
      commit('setStatistics', stats)
      
      // Trả về cấu trúc dữ liệu mà component mong đợi
      return {
        records: records,
        statistics: stats
      }
    } catch (error) {
      console.error('Lỗi khi lấy dữ liệu chấm công:', error)
      throw error
    }
  },
  
  // Giữ nguyên alias cho fetchAttendanceRecords để tương thích ngược
  async fetchAttendanceRecords({ dispatch, state }) {
    return dispatch('fetchRecords', state.filters)
  },
  
  // Thêm action để lấy danh sách địa điểm chấm công
  async fetchLocations({ commit }) {
    commit('setLoading', true)
    try {
      const response = await axios.get('/api/attendance/locations')
      commit('setLocations', response.data)
      return response.data
    } catch (error) {
      console.error('Lỗi khi lấy danh sách điểm chấm công:', error)
      throw error
    } finally {
      commit('setLoading', false)
    }
  },

  // Lấy chi tiết một bản ghi chấm công - Đổi tên để tương thích với component
  async fetchRecord({ commit }, recordId) {
    try {
      const response = await axios.get(`/attendance/${recordId}`)
      commit('setCurrentRecord', response.data)
      return response.data
    } catch (error) {
      console.error('Lỗi khi lấy chi tiết chấm công:', error)
      throw error
    }
  },

  // Tự động tính toán ca làm việc
  async calculateShift({ commit }, recordId) {
    try {
      const response = await axios.post(`/attendance/${recordId}/calculate-shift`)
      commit('updateAttendanceRecord', response.data)
      return response.data
    } catch (error) {
      console.error('Lỗi khi tính toán ca làm việc:', error)
      throw error
    }
  },

  // Cập nhật thông tin chấm công
  async updateAttendanceRecord({ commit }, { id, data }) {
    try {
      const response = await axios.put(`/attendance/${id}`, data)
      commit('updateAttendanceRecord', response.data)
      return response.data
    } catch (error) {
      console.error('Lỗi khi cập nhật chấm công:', error)
      throw error
    }
  },

  // Xuất dữ liệu chấm công - Thêm alias exportData
  async exportData({ state }, options) {
    const format = options.format || 'excel'
    const filters = {
      ...state.filters,
      ...options
    }
    
    try {
      const response = await axios.get('/attendance/export', {
        params: {
          ...filters,
          format
        },
        responseType: 'blob'
      })
      
      // Tạo URL để tải file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `attendance-${moment().format('YYYY-MM-DD')}.${format}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      return true
    } catch (error) {
      console.error('Lỗi khi xuất dữ liệu:', error)
      throw error
    }
  }
}

const mutations = {
  setLoading(state, loading) {
    state.loading = loading
  },
  setAttendanceRecords(state, records) {
    state.attendanceRecords = records
  },
  setCurrentRecord(state, record) {
    state.currentRecord = record
  },
  setFilters(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  setStatistics(state, statistics) {
    state.statistics = statistics
  },
  updateAttendanceRecord(state, updatedRecord) {
    const index = state.attendanceRecords.findIndex(r => r.id === updatedRecord.id)
    if (index !== -1) {
      state.attendanceRecords.splice(index, 1, updatedRecord)
    }
    if (state.currentRecord?.id === updatedRecord.id) {
      state.currentRecord = updatedRecord
    }
  },
  // Thêm mutation để lưu trữ danh sách địa điểm
  setLocations(state, locations) {
    state.locations = locations
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}